import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaSearch,
  FaShoppingCart,
  FaSync,
  FaCheckCircle,
  FaClock,
  FaCalendar,
  FaTimes,
  FaDownload,
  FaEnvelope,
  FaFileInvoiceDollar,
  FaExclamationTriangle,
  FaFileArchive,
  FaRedo,
  FaPrint,
  FaPhone,
  FaChevronLeft,
  FaChevronRight,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaList,
  FaBars,
  FaKeyboard,
  FaCircle,
} from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import {
  fetchAdminOrders,
  fetchAdminOrderById,
  downloadAdminInvoice,
  resendInvoiceEmail,
  retryInvoice,
  fetchFailedInvoices,
  exportInvoices,
  fetchOrderStats,
  fetchRevenueStats,
} from '../services/apiService';
import { getArticleImageUrl } from '../utils/imageUtils';
import { formatMoneyCHF } from '../utils/priceUtils';
import { HiOutlinePhoto } from 'react-icons/hi2';

const PAGE_SIZE = 20;
const STALE_HOURS = 24;
const DENSITY_KEY = 'orderManager.density';

// --- helpers ---

// Canonical CHF formatting ("CHF 1'234.50", symbol first, Swiss grouping).
const formatMoney = (amount) => formatMoneyCHF(amount);

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const customerName = (o) => {
  const c = o?.customer || {};
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || '-';
};

const paymentStatusColor = (status) => {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'voided':
      return 'bg-gray-200 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const isStale = (order) => {
  if (order?.status?.paymentStatus !== 'pending') return false;
  if (!order?.createdAt) return false;
  const ageMs = Date.now() - new Date(order.createdAt).getTime();
  return ageMs > STALE_HOURS * 3600 * 1000;
};

const toDateStr = (d) => d.toISOString().split('T')[0];

// Resolve a date-range preset (or custom inputs) into {from, to} YYYY-MM-DD
// strings sent to the API. Mirrors the export picker / analytics date math so
// behaviour stays consistent across the admin. `undefined` means "no bound".
const computeDateRange = (preset, customFrom, customTo) => {
  if (preset === 'custom') {
    return { from: customFrom || undefined, to: customTo || undefined };
  }
  const now = new Date();
  const to = toDateStr(now);
  const from = new Date(now);
  switch (preset) {
    case 'today':
      return { from: to, to };
    case '7d':
      from.setDate(from.getDate() - 7);
      return { from: toDateStr(from), to };
    case '30d':
      from.setDate(from.getDate() - 30);
      return { from: toDateStr(from), to };
    case '90d':
      from.setDate(from.getDate() - 90);
      return { from: toDateStr(from), to };
    case 'year':
      return { from: `${now.getFullYear()}-01-01`, to };
    case 'all':
    default:
      return { from: undefined, to: undefined };
  }
};

// --- presentational subcomponents ---

const StatusTab = ({ active, label, count, color, onClick }) => (
  <button
    onClick={onClick}
    className={`relative inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150 border ${
      active
        ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
    }`}
  >
    {color && (
      <FaCircle
        className={`text-[8px] ${active ? 'text-white/80' : color}`}
      />
    )}
    <span>{label}</span>
    {typeof count === 'number' && (
      <span
        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
          active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
        }`}
      >
        {count.toLocaleString()}
      </span>
    )}
  </button>
);

const KpiCard = ({ label, value, sub, accent = 'indigo', badge }) => {
  const accentMap = {
    indigo: 'text-indigo-700 bg-indigo-50',
    green: 'text-green-700 bg-green-50',
    amber: 'text-amber-700 bg-amber-50',
    rose: 'text-rose-700 bg-rose-50',
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">
            {label}
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        <span
          className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-md ${accentMap[accent]}`}
        >
          {badge}
        </span>
      </div>
    </div>
  );
};

const SortIcon = ({ active, dir }) => {
  if (!active) return <FaSort className="inline-block ml-1 text-gray-300" />;
  return dir === 'asc' ? (
    <FaSortUp className="inline-block ml-1 text-indigo-600" />
  ) : (
    <FaSortDown className="inline-block ml-1 text-indigo-600" />
  );
};

const Spinner = ({ className = 'h-4 w-4 text-current' }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v8H4z"
    />
  </svg>
);

// --- main component ---

const OrderManager = () => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [orderStatus, setOrderStatus] = useState('all');
  const [showFailedOnly, setShowFailedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [activeRow, setActiveRow] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [invoiceMsg, setInvoiceMsg] = useState(null);
  const [failedInvoiceCount, setFailedInvoiceCount] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportPicker, setShowExportPicker] = useState(false);
  const [statsByStatus, setStatsByStatus] = useState({});
  const [kpis, setKpis] = useState({
    revenue: 0,
    orders: 0,
    avgOrderValue: 0,
  });
  const [sort, setSort] = useState({ key: 'createdAt', dir: 'desc' });
  const [density, setDensity] = useState(() => {
    try {
      return localStorage.getItem(DENSITY_KEY) || 'comfortable';
    } catch {
      return 'comfortable';
    }
  });
  const [itemFilter, setItemFilter] = useState('');
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [dateRange, setDateRange] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const searchRef = useRef(null);
  const keyNavRef = useRef(false);

  // Debounce search input
  useEffect(() => {
    const id = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Persist density
  useEffect(() => {
    try {
      localStorage.setItem(DENSITY_KEY, density);
    } catch {
      /* ignore */
    }
  }, [density]);

  // Resolve the active date filter to {from, to} strings for the API call.
  const { from: dateFrom, to: dateTo } = useMemo(
    () => computeDateRange(dateRange, customFrom, customTo),
    [dateRange, customFrom, customTo]
  );

  const loadOrders = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await fetchAdminOrders({
        page,
        limit: PAGE_SIZE,
        paymentStatus,
        orderStatus,
        search: searchTerm,
        from: dateFrom,
        to: dateTo,
        sortBy: sort.key,
        sortDir: sort.dir,
        failedInvoiceOnly: showFailedOnly,
      });
      const payload = res?.data || {};
      setOrders(Array.isArray(payload.items) ? payload.items : []);
      setTotalOrders(payload.total || 0);
      setTotalPages(payload.totalPages || 1);
      setActiveRow(0);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        (t && t('orders.error')) ||
        'Failed to load orders';
      setError(msg);
      setOrders([]);
      setTotalOrders(0);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [page, paymentStatus, orderStatus, searchTerm, dateFrom, dateTo, sort, showFailedOnly, t]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Stats: count by payment status (used by tab badges) + last-7-days KPIs
  const loadStats = useCallback(async () => {
    const now = new Date();
    const to = now.toISOString().split('T')[0];
    const fromDate = new Date(now);
    fromDate.setDate(fromDate.getDate() - 7);
    const from = fromDate.toISOString().split('T')[0];

    const [ordRes, revRes] = await Promise.allSettled([
      fetchOrderStats(from, to),
      fetchRevenueStats(from, to, 'day'),
    ]);
    if (ordRes.status === 'fulfilled' && ordRes.value?.data) {
      setStatsByStatus(ordRes.value.data.byStatus || {});
    }
    if (revRes.status === 'fulfilled' && revRes.value?.data?.totals) {
      setKpis(revRes.value.data.totals);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Failed invoices count
  const refreshFailedCount = useCallback(() => {
    fetchFailedInvoices()
      .then((res) => setFailedInvoiceCount(res?.data?.count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshFailedCount();
  }, [refreshFailedCount]);

  // Scroll the active row into view when navigation came from the keyboard
  useEffect(() => {
    if (!keyNavRef.current) return;
    keyNavRef.current = false;
    if (typeof document === 'undefined') return;
    const el = document.querySelector(
      `[data-order-row-index="${activeRow}"]`
    );
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [activeRow]);

  // Sorting and the failed-invoice filter now run server-side (see loadOrders),
  // so the current page already reflects them and the pagination totals stay
  // correct. Render the page exactly as the API returned it.
  const visibleOrders = orders;

  // Discover orderStatus values present on current page (dynamic dropdown)
  const orderStatusOptions = useMemo(() => {
    const set = new Set();
    orders.forEach((o) => {
      const s = o?.status?.orderStatus;
      if (s) set.add(s);
    });
    return Array.from(set);
  }, [orders]);

  const openOrder = useCallback(async (order, index = -1) => {
    setSelectedOrder(order);
    setSelectedIndex(index);
    setItemFilter('');
    setInvoiceMsg(null);
    try {
      setIsLoadingDetail(true);
      const res = await fetchAdminOrderById(order._id);
      if (res?.data) setSelectedOrder(res.data);
    } catch (err) {
      console.error('Failed to fetch order details:', err);
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  const closeDrawer = useCallback(() => {
    setSelectedOrder(null);
    setSelectedIndex(-1);
    setItemFilter('');
  }, []);

  const navigateOrder = useCallback(
    (delta) => {
      if (selectedIndex < 0) return;
      const next = selectedIndex + delta;
      if (next < 0 || next >= visibleOrders.length) return;
      openOrder(visibleOrders[next], next);
    },
    [selectedIndex, visibleOrders, openOrder]
  );

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setPaymentStatus('all');
    setOrderStatus('all');
    setShowFailedOnly(false);
    setDateRange('all');
    setCustomFrom('');
    setCustomTo('');
    setPage(1);
  };

  const handleDownloadInvoice = async (order) => {
    setInvoiceLoading(true);
    setInvoiceMsg(null);
    try {
      await downloadAdminInvoice(order._id, order.orderNumber);
    } catch {
      setInvoiceMsg({
        type: 'error',
        text: t('orders.invoice.msgDownloadFailed'),
      });
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleRetryInvoice = async (order) => {
    setRetryLoading(true);
    setInvoiceMsg(null);
    try {
      await retryInvoice(order._id);
      setInvoiceMsg({
        type: 'success',
        text: t('orders.invoice.msgGenerated'),
      });
      const res = await fetchAdminOrderById(order._id);
      if (res?.data) setSelectedOrder(res.data);
      refreshFailedCount();
    } catch (err) {
      setInvoiceMsg({
        type: 'error',
        text:
          err?.response?.data?.error || t('orders.invoice.msgRetryFailed'),
      });
    } finally {
      setRetryLoading(false);
    }
  };

  const handleResendInvoice = async (order) => {
    setResendLoading(true);
    setInvoiceMsg(null);
    try {
      const res = await resendInvoiceEmail(order._id);
      setInvoiceMsg({
        type: 'success',
        text: t('orders.invoice.msgSentTo', {
          email: res?.data?.sentTo || order.customer?.email,
        }),
      });
    } catch {
      setInvoiceMsg({
        type: 'error',
        text: t('orders.invoice.msgEmailFailed'),
      });
    } finally {
      setResendLoading(false);
    }
  };

  const handleExportInvoices = async (preset) => {
    setExportLoading(true);
    const now = new Date();
    let from;
    const to = now.toISOString().split('T')[0];

    switch (preset) {
      case 'today':
        from = to;
        break;
      case 'week': {
        const d = new Date(now);
        d.setDate(d.getDate() - 7);
        from = d.toISOString().split('T')[0];
        break;
      }
      case 'month': {
        const d = new Date(now);
        d.setMonth(d.getMonth() - 1);
        from = d.toISOString().split('T')[0];
        break;
      }
      case 'year': {
        const d = new Date(now);
        d.setFullYear(d.getFullYear() - 1);
        from = d.toISOString().split('T')[0];
        break;
      }
      default:
        from = to;
    }

    try {
      await exportInvoices(from, to);
    } catch {
      setError(t('orders.invoice.msgExportFailed'));
    } finally {
      setExportLoading(false);
      setShowExportPicker(false);
    }
  };

  const toggleSort = (key) => {
    // Sorting is server-side now — reset to page 1 so we don't land past the
    // last page of the re-sorted result set.
    setPage(1);
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: 'createdAt', dir: 'desc' };
    });
  };

  const handlePrintOrder = () => {
    const source = document.querySelector('.om-printable');
    if (!source) {
      window.print();
      return;
    }

    // Build a self-contained iframe so the print job contains ONLY the order
    // content. Avoids ancestor layout boxes (sidebar, app shell, etc.) leaking
    // blank pages into the print output.
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText =
      'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);

    const styleNodes = Array.from(
      document.querySelectorAll('link[rel="stylesheet"], style')
    )
      .map((el) => el.outerHTML)
      .join('\n');

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      document.body.removeChild(iframe);
      window.print();
      return;
    }

    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8">${styleNodes}
      <style>
        @page { margin: 12mm; size: auto; }
        html, body { margin: 0; padding: 0; background: #fff; }
        body { font-family: inherit; }
        .om-no-print, .om-no-print * { display: none !important; }
        .om-printable {
          position: static !important;
          inset: auto !important;
          transform: none !important;
          width: 100% !important;
          max-width: 100% !important;
          height: auto !important;
          max-height: none !important;
          overflow: visible !important;
          background: #fff !important;
          box-shadow: none !important;
          border: 0 !important;
          border-radius: 0 !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        .om-printable .om-print-item {
          break-inside: avoid;
          page-break-inside: avoid;
        }
      </style></head><body>${source.outerHTML}</body></html>`);
    doc.close();

    const cleanup = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    const triggerPrint = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        // Give the browser time to open the dialog before removing the frame
        setTimeout(cleanup, 1000);
      }
    };

    if (doc.readyState === 'complete') {
      setTimeout(triggerPrint, 50);
    } else {
      iframe.onload = () => setTimeout(triggerPrint, 50);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const isTyping =
        tag === 'input' || tag === 'textarea' || tag === 'select';

      if (e.key === 'Escape') {
        if (selectedOrder) {
          closeDrawer();
          e.preventDefault();
        } else if (showShortcuts) {
          setShowShortcuts(false);
        } else if (
          searchInput ||
          paymentStatus !== 'all' ||
          orderStatus !== 'all' ||
          showFailedOnly ||
          dateRange !== 'all'
        ) {
          handleClearFilters();
        }
        return;
      }

      if (selectedOrder) {
        if (e.key === 'ArrowLeft' || e.key === '[') {
          navigateOrder(-1);
          e.preventDefault();
        } else if (e.key === 'ArrowRight' || e.key === ']') {
          navigateOrder(1);
          e.preventDefault();
        }
        return;
      }

      if (isTyping) return;

      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === '?') {
        setShowShortcuts((s) => !s);
      } else if (e.key === 'r' || e.key === 'R') {
        loadOrders();
      } else if (e.key === 'j' || e.key === 'ArrowDown') {
        keyNavRef.current = true;
        setActiveRow((i) => Math.min(visibleOrders.length - 1, i + 1));
        e.preventDefault();
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        keyNavRef.current = true;
        setActiveRow((i) => Math.max(0, i - 1));
        e.preventDefault();
      } else if (e.key === 'Enter') {
        const row = visibleOrders[activeRow];
        if (row) openOrder(row, activeRow);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedOrder,
    visibleOrders,
    activeRow,
    searchInput,
    paymentStatus,
    orderStatus,
    showFailedOnly,
    dateRange,
    showShortcuts,
  ]);

  const tabs = [
    {
      key: 'all',
      label: t('orders.search.allStatus'),
      color: 'text-gray-400',
      count:
        (statsByStatus.paid || 0) +
        (statsByStatus.pending || 0) +
        (statsByStatus.failed || 0) +
        (statsByStatus.voided || 0),
    },
    {
      key: 'pending',
      label: t('orders.search.pending'),
      color: 'text-yellow-500',
      count: statsByStatus.pending,
    },
    {
      key: 'paid',
      label: t('orders.search.completed'),
      color: 'text-green-500',
      count: statsByStatus.paid,
    },
    {
      key: 'failed',
      label: t('orders.search.cancelled'),
      color: 'text-red-500',
      count: statsByStatus.failed,
    },
    {
      key: 'voided',
      label: t('orders.tabs.voided'),
      color: 'text-gray-400',
      count: statsByStatus.voided,
    },
  ];

  const filteredItems = useMemo(() => {
    const items = selectedOrder?.items || [];
    if (!itemFilter.trim()) return items;
    const q = itemFilter.trim().toLowerCase();
    return items.filter((it) =>
      String(it?.nameAtPurchase || '').toLowerCase().includes(q)
    );
  }, [selectedOrder, itemFilter]);

  const rowPad = density === 'compact' ? 'py-2' : 'py-4';
  const filtersActive =
    searchTerm ||
    paymentStatus !== 'all' ||
    orderStatus !== 'all' ||
    showFailedOnly ||
    dateRange !== 'all';

  return (
    <div className="bg-white">
      {/* Print helper styles. The "Print" button in the drawer uses a
          dedicated iframe (see handlePrintOrder), so this block only acts as
          a sane fallback when the user presses Cmd/Ctrl+P on the page. */}
      <style>{`
        @media print {
          @page { margin: 12mm; size: auto; }
          .om-no-print, .om-no-print * { display: none !important; }
          .om-print-item { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      <div className="mx-auto max-w-[95%] px-2 sm:px-4 lg:px-6 om-no-print">
        {/* Header */}
        <div className="border-b border-gray-200 pb-6 pt-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div className="flex items-center gap-4 mb-4 lg:mb-0">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                <FaShoppingCart className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                  {t('orders.title')}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {t('orders.subtitle')}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowShortcuts(true)}
                className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-all duration-200 shadow-sm"
                title={t('orders.shortcutsTooltip')}
              >
                <FaKeyboard className="text-sm" />
                <span className="hidden sm:inline">
                  {t('orders.shortcutsButton')}
                </span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowExportPicker(!showExportPicker)}
                  disabled={exportLoading}
                  className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-50 transition-all duration-200 shadow-sm disabled:opacity-60"
                >
                  {exportLoading ? (
                    <Spinner className="h-4 w-4 text-gray-500" />
                  ) : (
                    <FaFileArchive className="text-sm" />
                  )}
                  {t('orders.export.button')}
                </button>
                {showExportPicker && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    {[
                      { key: 'today', label: t('orders.export.today') },
                      { key: 'week', label: t('orders.export.week') },
                      { key: 'month', label: t('orders.export.month') },
                      { key: 'year', label: t('orders.export.year') },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => handleExportInvoices(opt.key)}
                        className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 first:rounded-t-lg last:rounded-b-lg transition-colors"
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => {
                  loadOrders();
                  loadStats();
                  refreshFailedCount();
                }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2.5 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FaSync className="text-sm" />
                {t('orders.refreshOrders')}
              </button>
            </div>
          </div>

          {/* KPI strip — last 7 days, real totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KpiCard
              label={t('orders.kpi.orders')}
              value={(kpis.orders || 0).toLocaleString()}
              accent="indigo"
              sub={t('orders.kpi.last7Days')}
              badge={t('orders.kpi.badge')}
            />
            <KpiCard
              label={t('orders.kpi.revenue')}
              value={formatMoney(kpis.revenue || 0)}
              accent="green"
              sub={t('orders.kpi.last7Days')}
              badge={t('orders.kpi.badge')}
            />
            <KpiCard
              label={t('orders.kpi.avgOrderValue')}
              value={formatMoney(kpis.avgOrderValue || 0)}
              accent="indigo"
              sub={t('orders.kpi.last7Days')}
              badge={t('orders.kpi.badge')}
            />
            <KpiCard
              label={t('orders.kpi.open')}
              value={(statsByStatus.pending || 0).toLocaleString()}
              accent="amber"
              sub={t('orders.kpi.paymentPending')}
              badge={t('orders.kpi.badge')}
            />
          </div>

          {/* Status pipeline tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {tabs.map((tab) => (
              <StatusTab
                key={tab.key}
                active={paymentStatus === tab.key && !showFailedOnly}
                label={tab.label}
                color={tab.color}
                count={tab.count}
                onClick={() => {
                  setPaymentStatus(tab.key);
                  setShowFailedOnly(false);
                  setPage(1);
                }}
              />
            ))}
          </div>

          {/* Failed invoices banner — clickable to filter */}
          {failedInvoiceCount > 0 && (
            <button
              onClick={() => {
                setShowFailedOnly((s) => !s);
                setPaymentStatus('all');
                setPage(1);
              }}
              className={`w-full mb-4 p-4 rounded-xl flex items-center justify-between text-left transition-all ${
                showFailedOnly
                  ? 'bg-red-100 border-2 border-red-300 shadow-sm'
                  : 'bg-red-50 border border-red-200 hover:bg-red-100'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-lg">
                  <FaExclamationTriangle className="text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-red-900">
                    {t(
                      failedInvoiceCount === 1
                        ? 'orders.failedBanner.messageOne'
                        : 'orders.failedBanner.messageMany',
                      { count: failedInvoiceCount }
                    )}
                  </p>
                  <p className="text-sm text-red-700">
                    {showFailedOnly
                      ? t('orders.failedBanner.hintActive')
                      : t('orders.failedBanner.hintInactive')}
                  </p>
                </div>
              </div>
              <span className="text-red-700 text-sm font-medium">
                {showFailedOnly
                  ? t('orders.failedBanner.actionClear')
                  : t('orders.failedBanner.actionShow')}
              </span>
            </button>
          )}

          {/* Search + secondary filters */}
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                ref={searchRef}
                type="text"
                placeholder={`${t('orders.search.placeholder')}  (${t(
                  'orders.searchHint'
                )})`}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <FaCalendar className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <select
                  value={dateRange}
                  onChange={(e) => {
                    setDateRange(e.target.value);
                    setPage(1);
                  }}
                  className="border border-gray-300 rounded-lg pl-8 pr-3 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-sm"
                  title={t('orders.filters.dateTitle')}
                >
                  <option value="all">{t('orders.filters.dateAll')}</option>
                  <option value="today">{t('orders.filters.dateToday')}</option>
                  <option value="7d">{t('orders.filters.date7d')}</option>
                  <option value="30d">{t('orders.filters.date30d')}</option>
                  <option value="90d">{t('orders.filters.date90d')}</option>
                  <option value="year">{t('orders.filters.dateYear')}</option>
                  <option value="custom">{t('orders.filters.dateCustom')}</option>
                </select>
              </div>

              {dateRange === 'custom' && (
                <div className="inline-flex items-center gap-2">
                  <input
                    type="date"
                    value={customFrom}
                    max={customTo || undefined}
                    onChange={(e) => {
                      setCustomFrom(e.target.value);
                      setPage(1);
                    }}
                    aria-label={t('orders.filters.dateFrom')}
                    title={t('orders.filters.dateFrom')}
                    className="border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-sm"
                  />
                  <span className="text-gray-400 text-sm">–</span>
                  <input
                    type="date"
                    value={customTo}
                    min={customFrom || undefined}
                    onChange={(e) => {
                      setCustomTo(e.target.value);
                      setPage(1);
                    }}
                    aria-label={t('orders.filters.dateTo')}
                    title={t('orders.filters.dateTo')}
                    className="border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-sm"
                  />
                </div>
              )}

              {orderStatusOptions.length > 0 && (
                <select
                  value={orderStatus}
                  onChange={(e) => {
                    setOrderStatus(e.target.value);
                    setPage(1);
                  }}
                  className="border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-sm"
                  title={t('orders.filters.orderStatusTitle')}
                >
                  <option value="all">
                    {t('orders.filters.orderStatusAll')}
                  </option>
                  {orderStatusOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              )}

              <div className="inline-flex rounded-lg border border-gray-300 bg-white overflow-hidden">
                <button
                  onClick={() => setDensity('comfortable')}
                  className={`px-3 py-2.5 text-sm flex items-center gap-1.5 ${
                    density === 'comfortable'
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  title={t('orders.filters.densityComfortable')}
                >
                  <FaBars className="text-xs" />
                </button>
                <button
                  onClick={() => setDensity('compact')}
                  className={`px-3 py-2.5 text-sm flex items-center gap-1.5 ${
                    density === 'compact'
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  title={t('orders.filters.densityCompact')}
                >
                  <FaList className="text-xs" />
                </button>
              </div>

              {filtersActive && (
                <button
                  onClick={handleClearFilters}
                  className="px-4 py-2.5 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 text-sm"
                >
                  {t('orders.search.clearFilters')}
                </button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 mt-4">
            {error}
          </div>
        )}

        <section aria-labelledby="orders-heading" className="pb-24 pt-6">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="flex items-center gap-3">
                <Spinner className="h-8 w-8 text-indigo-600" />
                <span className="text-gray-500">{t('orders.loading')}</span>
              </div>
            </div>
          ) : visibleOrders.length > 0 ? (
            <>
              <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th
                          onClick={() => toggleSort('orderNumber')}
                          className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                        >
                          {t('orders.table.orderId')}
                          <SortIcon
                            active={sort.key === 'orderNumber'}
                            dir={sort.dir}
                          />
                        </th>
                        <th
                          onClick={() => toggleSort('customer')}
                          className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                        >
                          {t('orders.table.customer')}
                          <SortIcon
                            active={sort.key === 'customer'}
                            dir={sort.dir}
                          />
                        </th>
                        <th
                          onClick={() => toggleSort('createdAt')}
                          className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                        >
                          {t('orders.table.date')}
                          <SortIcon
                            active={sort.key === 'createdAt'}
                            dir={sort.dir}
                          />
                        </th>
                        <th
                          onClick={() => toggleSort('status')}
                          className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                        >
                          {t('orders.table.status')}
                          <SortIcon
                            active={sort.key === 'status'}
                            dir={sort.dir}
                          />
                        </th>
                        <th
                          onClick={() => toggleSort('total')}
                          className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                        >
                          {t('orders.table.total')}
                          <SortIcon
                            active={sort.key === 'total'}
                            dir={sort.dir}
                          />
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('orders.table.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {visibleOrders.map((order, idx) => {
                        const stale = isStale(order);
                        const isActive = idx === activeRow;
                        return (
                          <motion.tr
                            key={order._id}
                            data-order-row-index={idx}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            onMouseEnter={() => setActiveRow(idx)}
                            onClick={() => openOrder(order, idx)}
                            className={`cursor-pointer transition-colors duration-150 ${
                              isActive
                                ? 'bg-indigo-50/60'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <td
                              className={`px-6 ${rowPad} whitespace-nowrap text-sm font-medium text-gray-900`}
                            >
                              <div className="flex items-center gap-2">
                                {stale && (
                                  <span
                                    className="w-2 h-2 rounded-full bg-amber-500 inline-block"
                                    title={t('orders.row.stale', {
                                      hours: STALE_HOURS,
                                    })}
                                  />
                                )}
                                <span>{order.orderNumber}</span>
                              </div>
                            </td>
                            <td
                              className={`px-6 ${rowPad} whitespace-nowrap text-sm text-gray-500`}
                            >
                              <div>{customerName(order)}</div>
                              <div className="text-xs text-gray-400">
                                {order?.customer?.email}
                              </div>
                            </td>
                            <td
                              className={`px-6 ${rowPad} whitespace-nowrap text-sm text-gray-500`}
                            >
                              <div className="flex items-center">
                                <FaCalendar className="mr-2 text-gray-400" />
                                {formatDate(order.createdAt)}
                              </div>
                            </td>
                            <td className={`px-6 ${rowPad} whitespace-nowrap`}>
                              <span
                                className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${paymentStatusColor(
                                  order?.status?.paymentStatus
                                )}`}
                              >
                                {order?.status?.paymentStatus || 'pending'}
                              </span>
                              {order?.status?.orderStatus && (
                                <span className="ml-2 px-2 py-0.5 inline-flex text-[10px] leading-4 font-medium rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                                  {order.status.orderStatus}
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-6 ${rowPad} whitespace-nowrap text-sm text-gray-700 font-medium`}
                            >
                              {formatMoney(
                                order?.totals?.totalAmount,
                                order?.totals?.currency
                              )}
                            </td>
                            <td
                              className={`px-6 ${rowPad} whitespace-nowrap text-right text-sm font-medium`}
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openOrder(order, idx);
                                }}
                                className="text-indigo-600 hover:text-indigo-900 inline-flex items-center gap-1 px-3 py-1 rounded-md hover:bg-indigo-50 transition-colors duration-200"
                              >
                                {t('orders.table.view')}
                              </button>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-gray-500">
                    {t('orders.pagination.summary', {
                      page,
                      totalPages,
                      total: totalOrders,
                    })}
                  </span>
                  <div className="flex gap-2">
                    <button
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="px-3 py-1.5 border rounded-md text-sm disabled:opacity-50"
                    >
                      <FaChevronLeft className="inline mr-1" />{' '}
                      {t('orders.pagination.prev')}
                    </button>
                    <button
                      disabled={page >= totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      className="px-3 py-1.5 border rounded-md text-sm disabled:opacity-50"
                    >
                      {t('orders.pagination.next')}{' '}
                      <FaChevronRight className="inline ml-1" />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900">
                {t('orders.empty.title')}
              </h3>
              <p className="text-gray-500 mt-1">
                {filtersActive
                  ? t('orders.empty.withFilters')
                  : t('orders.empty.noFilters')}
              </p>
            </div>
          )}
        </section>
      </div>

      {/* Side drawer for order details (replaces modal) */}
      <AnimatePresence>
        {selectedOrder && (
          <div className="fixed inset-0 z-50 overflow-hidden om-printable-root">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40 backdrop-blur-sm om-no-print"
              onClick={closeDrawer}
            />

            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 320 }}
              className="absolute top-0 right-0 h-full w-full sm:w-[560px] bg-white shadow-2xl border-l border-gray-200 overflow-y-auto om-printable"
            >
              {/* Drawer header */}
              <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-200">
                <div className="flex items-center justify-between p-5">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-md flex-shrink-0">
                      <FaShoppingCart className="text-white text-lg" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-lg font-bold text-gray-900 truncate">
                        {selectedOrder.orderNumber}
                      </h2>
                      <p className="text-xs text-gray-500">
                        {t('orders.modal.subtitle')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 om-no-print">
                    <button
                      onClick={() => navigateOrder(-1)}
                      disabled={selectedIndex <= 0}
                      className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t('orders.drawer.prevOrder')}
                    >
                      <FaChevronLeft />
                    </button>
                    <button
                      onClick={() => navigateOrder(1)}
                      disabled={
                        selectedIndex < 0 ||
                        selectedIndex >= visibleOrders.length - 1
                      }
                      className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t('orders.drawer.nextOrder')}
                    >
                      <FaChevronRight />
                    </button>
                    <button
                      onClick={handlePrintOrder}
                      className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
                      title={t('orders.drawer.print')}
                    >
                      <FaPrint />
                    </button>
                    <button
                      onClick={closeDrawer}
                      className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
                      title={t('orders.drawer.close')}
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-5 space-y-5">
                {isLoadingDetail && (
                  <div className="text-sm text-gray-500 flex items-center gap-2">
                    <Spinner className="h-4 w-4 text-gray-400" />
                    {t('orders.drawer.loadingDetails')}
                  </div>
                )}

                {/* Summary grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      {t('orders.modal.customer')}
                    </label>
                    <p className="text-gray-900 font-medium">
                      {customerName(selectedOrder)}
                    </p>
                    {selectedOrder?.customer?.email && (
                      <a
                        href={`mailto:${selectedOrder.customer.email}`}
                        className="text-sm text-indigo-600 hover:underline inline-flex items-center gap-1 om-no-print"
                      >
                        <FaEnvelope className="text-xs" />
                        {selectedOrder.customer.email}
                      </a>
                    )}
                    {selectedOrder?.customer?.email && (
                      <span className="hidden print:inline text-sm text-gray-700">
                        {selectedOrder.customer.email}
                      </span>
                    )}
                    {selectedOrder?.customer?.phone && (
                      <div className="mt-1">
                        <a
                          href={`tel:${selectedOrder.customer.phone}`}
                          className="text-sm text-indigo-600 hover:underline inline-flex items-center gap-1 om-no-print"
                        >
                          <FaPhone className="text-xs" />
                          {selectedOrder.customer.phone}
                        </a>
                        <span className="hidden print:inline text-sm text-gray-700">
                          {selectedOrder.customer.phone}
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      {t('orders.modal.date')}
                    </label>
                    <p className="text-gray-900 font-medium">
                      {formatDateTime(selectedOrder.createdAt)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      {t('orders.drawer.paymentStatus')}
                    </label>
                    <div className="flex flex-wrap gap-2 items-center">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${paymentStatusColor(
                          selectedOrder?.status?.paymentStatus
                        )}`}
                      >
                        {selectedOrder?.status?.paymentStatus || 'pending'}
                      </span>
                      {selectedOrder?.status?.orderStatus && (
                        <span className="px-2.5 py-0.5 text-[11px] font-medium rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                          {selectedOrder.status.orderStatus}
                        </span>
                      )}
                    </div>
                    {selectedOrder?.payment?.state && (
                      <p className="text-xs text-gray-500 mt-1">
                        PostFinance: {selectedOrder.payment.state}
                      </p>
                    )}
                    {selectedOrder?.payment?.paidAt && (
                      <p className="text-xs text-gray-500">
                        {t('orders.drawer.paidOn', {
                          date: formatDate(selectedOrder.payment.paidAt),
                        })}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      {t('orders.modal.total')}
                    </label>
                    <p className="text-gray-900 font-bold text-lg">
                      {formatMoney(
                        selectedOrder?.totals?.totalAmount,
                        selectedOrder?.totals?.currency
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('orders.drawer.subtotal')}{' '}
                      {formatMoney(
                        selectedOrder?.totals?.subtotal,
                        selectedOrder?.totals?.currency
                      )}{' '}
                      · {t('orders.drawer.shipping')}{' '}
                      {formatMoney(
                        selectedOrder?.totals?.shippingCost,
                        selectedOrder?.totals?.currency
                      )}
                    </p>
                  </div>
                </div>

                {/* Shipping address */}
                {selectedOrder?.shippingAddress && (
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {t('orders.drawer.deliveryAddress')}
                    </h3>
                    <p className="text-sm text-gray-700">
                      {selectedOrder.shippingAddress.street}
                      {selectedOrder.shippingAddress.houseNumber
                        ? ` ${selectedOrder.shippingAddress.houseNumber}`
                        : ''}
                    </p>
                    <p className="text-sm text-gray-700">
                      {selectedOrder.shippingAddress.postalCode}{' '}
                      {selectedOrder.shippingAddress.city}
                    </p>
                    <p className="text-sm text-gray-700">
                      {selectedOrder.shippingAddress.country}
                    </p>
                    {selectedOrder?.shippingMethod?.name && (
                      <p className="text-xs text-gray-500 mt-1">
                        {t('orders.drawer.via', {
                          method: selectedOrder.shippingMethod.name,
                          price: formatMoney(
                            selectedOrder.shippingMethod.price
                          ),
                        })}
                      </p>
                    )}
                  </div>
                )}

                {/* Items + filter */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-3 om-no-print">
                    <h3 className="font-semibold text-gray-900">
                      {t('orders.modal.orderItems')}{' '}
                      <span className="text-gray-400 font-normal text-sm">
                        ({(selectedOrder.items || []).length})
                      </span>
                    </h3>
                    {(selectedOrder.items || []).length > 4 && (
                      <div className="relative">
                        <FaSearch className="absolute left-2.5 top-2.5 text-gray-400 text-xs" />
                        <input
                          type="text"
                          value={itemFilter}
                          onChange={(e) => setItemFilter(e.target.value)}
                          placeholder={t('orders.drawer.searchItems')}
                          className="pl-7 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    {filteredItems.length === 0 ? (
                      <p className="text-sm text-gray-500 py-2">
                        {t('orders.drawer.noItemsMatch')}
                      </p>
                    ) : (
                      filteredItems.map((item, idx) => {
                        const imageUrl = getArticleImageUrl(item.image, 'F');
                        return (
                          <div
                            key={`${item.productId}-${idx}`}
                            className="om-print-item flex items-center gap-3 py-2 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="h-14 w-14 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center om-no-print">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={item.nameAtPurchase}
                                  className="max-h-full max-w-full object-contain"
                                />
                              ) : (
                                <HiOutlinePhoto className="w-8 h-8 text-gray-300" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">
                                {item.nameAtPurchase}
                              </p>
                              <p className="text-sm text-gray-500">
                                {t('orders.modal.quantity')}: {item.quantity}{' '}
                                × {formatMoney(item.unitPriceAtPurchase)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-gray-900">
                                {formatMoney(item.lineTotal)}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Total bar */}
                <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                  <p className="font-semibold text-gray-900">
                    {t('orders.modal.total')}
                  </p>
                  <p className="font-bold text-xl text-indigo-600">
                    {formatMoney(
                      selectedOrder?.totals?.totalAmount,
                      selectedOrder?.totals?.currency
                    )}
                  </p>
                </div>

                {/* Invoice section */}
                {selectedOrder?.payment?.transactionId && (
                  <div className="border-t border-gray-200 pt-4 om-no-print">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <FaFileInvoiceDollar className="text-indigo-600" />
                        <h3 className="font-semibold text-gray-900">
                          {t('orders.invoice.title')}
                        </h3>
                      </div>
                      {selectedOrder?.payment?.invoice?.status === 'saved' && (
                        <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          {t('orders.invoice.saved')}
                        </span>
                      )}
                      {selectedOrder?.payment?.invoice?.status === 'failed' && (
                        <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          {t('orders.invoice.failed')}
                        </span>
                      )}
                      {(!selectedOrder?.payment?.invoice?.status ||
                        selectedOrder?.payment?.invoice?.status ===
                          'pending') && (
                        <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          {t('orders.invoice.pending')}
                        </span>
                      )}
                    </div>

                    {selectedOrder?.payment?.invoice?.status === 'failed' &&
                      selectedOrder?.payment?.invoice?.error && (
                        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                          <p className="font-medium mb-1">
                            {t('orders.invoice.errorLabel')}
                          </p>
                          <p>{selectedOrder.payment.invoice.error}</p>
                        </div>
                      )}

                    <div className="text-sm text-gray-600 mb-3">
                      {selectedOrder?.payment?.invoice?.invoiceEmailSent ? (
                        <span className="inline-flex items-center gap-1.5 text-green-700">
                          <FaCheckCircle className="text-xs" />
                          {t('orders.invoice.emailSentOn', {
                            date: formatDate(
                              selectedOrder.payment.invoice.emailSentAt
                            ),
                          })}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-yellow-700">
                          <FaClock className="text-xs" />
                          {t('orders.invoice.emailNotSent')}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {selectedOrder?.payment?.invoice?.status === 'failed' && (
                        <button
                          onClick={() => handleRetryInvoice(selectedOrder)}
                          disabled={retryLoading}
                          className="inline-flex items-center gap-2 px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-all duration-200 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {retryLoading ? (
                            <>
                              <Spinner className="h-4 w-4 text-white" />
                              {t('orders.invoice.retrying')}
                            </>
                          ) : (
                            <>
                              <FaRedo className="text-xs" />
                              {t('orders.invoice.retry')}
                            </>
                          )}
                        </button>
                      )}

                      <button
                        onClick={() => handleDownloadInvoice(selectedOrder)}
                        disabled={invoiceLoading}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {invoiceLoading ? (
                          <>
                            <Spinner className="h-4 w-4 text-white" />
                            {t('orders.invoice.downloading')}
                          </>
                        ) : (
                          <>
                            <FaDownload className="text-xs" />
                            {t('orders.invoice.download')}
                          </>
                        )}
                      </button>

                      <button
                        onClick={() => handleResendInvoice(selectedOrder)}
                        disabled={resendLoading}
                        className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {resendLoading ? (
                          <>
                            <Spinner className="h-4 w-4 text-gray-500" />
                            {t('orders.invoice.sending')}
                          </>
                        ) : (
                          <>
                            <FaEnvelope className="text-xs" />
                            {t('orders.invoice.sendEmail')}
                          </>
                        )}
                      </button>
                    </div>

                    {invoiceMsg && (
                      <p
                        className={`text-sm mt-2 ${
                          invoiceMsg.type === 'success'
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        {invoiceMsg.text}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Keyboard shortcuts overlay */}
      <AnimatePresence>
        {showShortcuts && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center om-no-print"
            onClick={() => setShowShortcuts(false)}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 w-[420px] max-w-[90vw]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 text-lg">
                  {t('orders.shortcutsHelp.title')}
                </h3>
                <button
                  onClick={() => setShowShortcuts(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              </div>
              <ul className="text-sm text-gray-700 space-y-2">
                {[
                  ['/', t('orders.shortcutsHelp.focusSearch')],
                  ['j  /  ↓', t('orders.shortcutsHelp.nextRow')],
                  ['k  /  ↑', t('orders.shortcutsHelp.prevRow')],
                  ['Enter', t('orders.shortcutsHelp.openOrder')],
                  ['←  /  →', t('orders.shortcutsHelp.cycleOrders')],
                  ['r', t('orders.shortcutsHelp.refresh')],
                  ['?', t('orders.shortcutsHelp.help')],
                  ['Esc', t('orders.shortcutsHelp.close')],
                ].map(([k, label]) => (
                  <li
                    key={k}
                    className="flex items-center justify-between gap-3 py-1"
                  >
                    <span className="text-gray-600">{label}</span>
                    <kbd className="px-2 py-1 text-xs bg-gray-100 border border-gray-300 rounded font-mono">
                      {k}
                    </kbd>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OrderManager;
